import { useScroll, useTransform, motion, useMotionValueEvent } from "framer-motion";
import { useMemo, memo, useRef, useEffect } from "react";
import { SentencesOnScroll } from "../../components/sentences-scroll";
import { useInView } from "react-intersection-observer";
import { EASING_CURVES, ANIMATION_TIMING } from "../../utils/motion";
import { useSectionVisibility, useScrollContext } from "../../utils/ScrollContext";
import Round from "../../Round.svg";

const SAMIntroduction = memo(({ textes, setIsBackgroundWhite }) => {
  const targetRef = useRef(null);
  const { ref: sectionRef } = useSectionVisibility('sam-introduction', 0.3);
  const { getVisibleSections } = useScrollContext();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });
  const [ref] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  // Text span animations with mobile-responsive transforms
  const span1PosX = useTransform(
    scrollYProgress,
    [0, 0.1, 1],
    ["0%", "-70%", "-100%"]
  );
  const span1PosY = useTransform(
    scrollYProgress,
    [0, 0.1, 1],
    ["0%", "-70%", "-100%"]
  );
  const span2PosX = useTransform(scrollYProgress, [0, 0.1, 1], ["0%", "0%", "0%"]);
  const span2PosY = useTransform(
    scrollYProgress,
    [0, 0.1, 1],
    ["0%", "-400%", "-500%"]
  );
  const span3PosX = useTransform(
    scrollYProgress,
    [0, 0.1, 1],
    ["0%", "70%", "100%"]
  );
  const span3PosY = useTransform(
    scrollYProgress,
    [0, 0.1, 1],
    ["0%", "-70%", "-100%"]
  );

  const blur = useTransform(
    scrollYProgress,
    [0, 0.1, 1],
    ["blur(0px)", "blur(10px)", "blur(20px)"]
  );

  const bulletOpacity = useTransform(scrollYProgress, [0, 0.01, 1], [1, 0, 0]);

  useEffect(() => {
    const visibleSections = getVisibleSections();
    if (visibleSections.includes("sam-intro") && setIsBackgroundWhite) {
      setIsBackgroundWhite(false);
    }
  }, [getVisibleSections, setIsBackgroundWhite]);


  return (
    <section id="intro" aria-label="Introduction Studio SAM" ref={sectionRef}>
      <div className="flex flex-col mt-auto mb-auto justify-start items-center h-[140vh] mx-auto" >
        <div className="relative flex flex-row justify-center items-center item-ce w-full" ref={ref}>
          <motion.img
            src={Round}
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 6 }}
            className="absolute z-[100] top-[15vh]  -translate-x-1/2 
               w-[150px]  
               xs:w-[200px]        /* Taille de base (xs) */
               sm:w-[250px] 
               md:w-[300px] 
               lg:w-[320px] 
               xl:w-[400px]
               2xl:w-[480px]"
            exit={{ opacity: 0 }}
            alt="Logo Studio SAM animé en rotation"
          />
          <div className="w-48 flex-none" />
          <div className="w-48 flex-none" />
          <motion.h1
            className="font-sam text-white text-center leading-none

                 text-[10rem]      /* Base (xs) */
                 sm:text-[12rem]   /* sm */
                 md:text-[14rem]   /* md */
                 lg:text-[16rem]   /* lg */
                 xl:text-[20rem]   /* xl */
                 2xl:text-[25rem]  /* 2xl */
                 
                 relative 
                 mt-[25vh]
                 xs:mt-[20vh]
                 md:mt-[22vh]
                 sm:mt-[20vh]
                 lg:mt-[22vh]
                 xl:mt-[25vh]
                 xs:left-[-6rem] 
                 sm:left-[-3rem] 
                 md:left-[-3rem] 
                 lg:left-[-3rem] 
                 xl:left-[-3rem]
                 2xl:left-[1rem]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: ANIMATION_TIMING.durations.normal,
              delay: ANIMATION_TIMING.delays.medium,
              ease: EASING_CURVES.smooth
            }}
            itemProp="name"
          >
            SAM

          </motion.h1>
          <motion.div
            className="flex flex-col relative bottom-[25%] top-[-1vh]  xs:bottom-0 xs:top-0 xs:left-[-5rem] xs:relative xs:mt-[40rem] xs:text-left sm:mt-[32rem] sm:mr-[5rem] sm:text-left sm:bottom-[-10vh] md:mt-[25rem] md:bottom-[25%] lg:mt-[18vh] 3xl:mt-[18vh] leading-[20px] xs:leading-[15px] md:leading-[20px] lg:leading-[20px] xl:leading-[25px] 2xl:leading-[30px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: ANIMATION_TIMING.durations.normal,
              delay: ANIMATION_TIMING.delays.long,
              ease: EASING_CURVES.smooth
            }}
            role="heading"
            aria-level="2"
          >
            <p className="font-lexend text-[1.5rem] xs:text-[1.5rem] text-sam-cyan m-0 text-left ml-[10px] 3xl:text-[2rem] xs:text-[clamp(0.8rem,2vw,1rem)] xs:text-left xs:ml-0 sm:text-[clamp(1rem,2.5vw,1.2rem)] sm:text-left sm:ml-0 md:text-[clamp(1.2rem,3vw,1.5rem)] md:text-left lg:text-[clamp(1.5rem,3vw,2rem)] lg:text-left 2xl:text-[clamp(2rem,3vw,3rem)]">Studio</p>
           <p className="font-lexend text-[1.5rem] xs:text-[1.5rem] text-sam-cyan m-0 text-left ml-[10px] 3xl:text-[2rem] xs:text-[clamp(0.8rem,2vw,1rem)] xs:text-left xs:ml-0 sm:text-[clamp(1rem,2.5vw,1.2rem)] sm:text-left sm:ml-0 md:text-[clamp(1.2rem,3vw,1.5rem)] md:text-left lg:text-[clamp(1.5rem,3vw,2rem)] lg:text-left 2xl:text-[clamp(2rem,3vw,3rem)]">
              <b>animé</b>
            </p>
            <p className="font-lexend text-[1.5rem] xs:text-[1.5rem] text-sam-cyan m-0 text-left ml-[10px] 3xl:text-[2rem] xs:text-[clamp(0.8rem,2vw,1rem)] xs:text-left xs:ml-0 sm:text-[clamp(1rem,2.5vw,1.2rem)] sm:text-left sm:ml-0 md:text-[clamp(1.2rem,3vw,1.5rem)] md:text-left lg:text-[clamp(1.5rem,3vw,2rem)] lg:text-left 2xl:text-[clamp(2rem,3vw,3rem)]">
              et <b>mouvementé</b>
            </p>
          </motion.div>
        </div>
        <div className="flex items-center mt-[20rem] md:flex-col md:mt-[20rem] md:text-center sm:flex-col sm:flex-col-1 xs:flex-col xs:flex-col-1" role="list" aria-label="Services du studio">
          <motion.span
            style={{ x: span1PosX, y: span1PosY, filter: blur }}
            className="font-sam text-[clamp(2rem,8vw,6rem)] text-white m-[0_1rem] 3xl:text-[4rem] xs:text-[1.5rem] sm:text-[2rem] md:text-[2.5rem] lg:text-[3rem] 2xl:text-[4rem]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: ANIMATION_TIMING.durations.normal,
              delay: ANIMATION_TIMING.delays.extraLong,
              ease: EASING_CURVES.smooth
            }}
            role="listitem"
          >
            {textes?.mentra1}
          </motion.span>
          <motion.span
            style={{ opacity: bulletOpacity }}
            className="text-[3rem] text-sam-cyan m-[0_1rem] 3xl:text-[2rem] xs:text-[1.5rem] sm:text-[2rem] md:text-[2.5rem] lg:text-[3rem] 2xl:text-[4rem]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: ANIMATION_TIMING.durations.normal,
              delay: ANIMATION_TIMING.delays.extraLong,
              ease: EASING_CURVES.smooth
            }}
            aria-hidden="true"
          >
            &#8226;
          </motion.span>
          <motion.span
            style={{ x: span2PosX, y: span2PosY, filter: blur }}
            className="font-sam text-[clamp(2rem,8vw,6rem)] text-white m-[0_1rem] 3xl:text-[4rem] xs:text-[1.5rem] sm:text-[2rem] md:text-[2.5rem] lg:text-[3rem] 2xl:text-[4rem]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: ANIMATION_TIMING.durations.slow,
              delay: ANIMATION_TIMING.delays.extraLong,
              ease: EASING_CURVES.smooth
            }}
            role="listitem"
          >
            {textes?.mentra2}
          </motion.span>
          <motion.span
            style={{ opacity: bulletOpacity }}
            className="text-[3rem] text-sam-cyan m-[0_1rem] 3xl:text-[2rem] xs:text-[1.5rem] sm:text-[2rem] md:text-[2.5rem] lg:text-[3rem] 2xl:text-[4rem]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: ANIMATION_TIMING.durations.normal,
              delay: ANIMATION_TIMING.delays.extraLong,
              ease: EASING_CURVES.smooth
            }}
            aria-hidden="true"
          >
            &#8226;
          </motion.span>
          <motion.span
            style={{ x: span3PosX, y: span3PosY, filter: blur }}
            className="font-sam text-[clamp(2rem,8vw,6rem)] text-white m-[0_1rem] 3xl:text-[4rem] xs:text-[1.5rem] sm:text-[2rem] md:text-[2.5rem] lg:text-[3rem] 2xl:text-[4rem]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: ANIMATION_TIMING.durations.slow,
              delay: ANIMATION_TIMING.delays.extraLong,
              ease: EASING_CURVES.smooth
            }}
            role="listitem"
          >
            {textes?.mentra3}
          </motion.span>
        </div>
      </div>
      <div className="relative h-[40vh]" ref={targetRef}>

      </div>
    </section>
  );
});

SAMIntroduction.displayName = 'SAMIntroduction';


export default SAMIntroduction;
